import { Flex } from 'components/Flex';
import { BKJButton } from '@bookjane2/bookjane-design-library';
import { FC, MouseEventHandler } from 'react';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { QuerySwitch } from 'components/QuerySwitch';
import {
  RemovePositionDetailsHeaderWrapper,
  H3,
  PositionName,
  Text,
  TextLabel,
  RemovePositionDetailsWrapper,
  RemovePositionDetailsContent,
  RemovePositionText,
  RemovePositionDetailsLocationWrapper,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.styled';
import { useTranslation } from 'hooks/useTranslation';
import {
  useRemovePositionDataContext,
  RemovePositionDataProvider,
  RemovePositionDataContext,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/views/RemovePosition.data.context';
import {
  removePositionModalRoutingService,
  useRemovePositionModalUIContext,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { formatPlural } from 'utils/formatPlural';

const RemovePositionComponent: FC = () => {
  const { data } = useRemovePositionDataContext();
  const { locationId, goToNextView } = useRemovePositionModalUIContext();

  const { t } = useTranslation();

  const location = data?.fetchPosition?.serviceableCommunities.find(
    (community) => community.displayId === locationId,
  );
  const teamMembersCount = data?.fetchPosition?.teamMembers?.totalCount;
  const openPositionsCount = data?.fetchPosition?.configuredShifts?.totalCount;
  const removePositionFlowType =
    data?.fetchPosition?.teamMembers?.totalCount > 0 ? 'JaneConnection' : 'OrderConnection';
  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
    >
      <RemovePositionDetailsHeaderWrapper>
        <H3>{t('location_settings:UNABLE_TO_REMOVE_POSITION')}</H3>
        <PositionName>
          {t('common:POSITION')}: {data?.fetchPosition?.name}
        </PositionName>
        <RemovePositionDetailsLocationWrapper>
          <TextLabel>{t('common:LOCATION')}</TextLabel>
          <Text>{location?.name}</Text>
        </RemovePositionDetailsLocationWrapper>
      </RemovePositionDetailsHeaderWrapper>
      <RemovePositionDetailsWrapper>
        <RemovePositionDetailsContent>
          {t('location_settings:REMOVE_POSITION_MODAL_MESSAGE')}
        </RemovePositionDetailsContent>
        <RemovePositionDetailsContent>
          {teamMembersCount > 0 && (
            <RemovePositionText>
              <b>
                {`${t('location_settings:REMOVE_POSITION_MODAL_TM_COUNT', {
                  team_members_count: teamMembersCount,
                })} ${formatPlural(t('create_shifts:MEMBER').toLowerCase(), teamMembersCount)}`}
              </b>
              &nbsp;
              {t(
                `location_settings:${
                  teamMembersCount === 1
                    ? 'REMOVE_POSITION_MODAL_SINGLE_TM_TEXT'
                    : 'REMOVE_POSITION_MODAL_TM_COUNT_TEXT'
                }`,
              )}
            </RemovePositionText>
          )}
          {openPositionsCount > 0 && (
            <RemovePositionText>
              <b>
                {`${t('location_settings:REMOVE_POSITION_MODAL_OPEN_POSITION_COUNT', {
                  open_positions_count: openPositionsCount,
                })} ${formatPlural(t('schedule:SHIFT').toLowerCase(), openPositionsCount)}`}
              </b>
              &nbsp;
              {t(
                `location_settings:${
                  openPositionsCount === 1
                    ? 'REMOVE_POSITION_MODAL_SINGLE_OPEN_POSITION_COUNT_TEXT'
                    : 'REMOVE_POSITION_MODAL_OPEN_POSITION_COUNT_TEXT'
                }`,
              )}
            </RemovePositionText>
          )}
        </RemovePositionDetailsContent>
        <RemovePositionDetailsContent>
          {t('location_settings:REMOVE_POSITION_MODAL_NEXT_STEP_MESSAGE')}
        </RemovePositionDetailsContent>
      </RemovePositionDetailsWrapper>
      <Flex height="100%"></Flex>
      <BKJSideModalFooter justifyContent="flex-end" gap="8px">
        <BKJButton
          variant="PurpleOutlined"
          onClick={() => removePositionModalRoutingService.close()}
          type="button"
          width="180px"
        >
          {t('auth:CANCEL')}
        </BKJButton>
        <BKJButton
          variant="PurpleSolid"
          width="180px"
          onClick={() =>
            goToNextView({
              removePositionFlowType,
            }) as unknown as MouseEventHandler<HTMLButtonElement>
          }
          type="button"
        >
          {t('groups:NEXT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Flex>
  );
};

export const RemovePositionView: FC = () => {
  return (
    <RemovePositionDataProvider>
      <QuerySwitch context={RemovePositionDataContext} component={RemovePositionComponent} />
    </RemovePositionDataProvider>
  );
};
